/**
 * Abrechnungen (Statements) je Kunde und Periode — Liste mit Filtern.
 * Backend: GET /api/billing/statements?period=&status=
 */
import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { StatementSummary, extractApiError, listStatements } from '../services/api';
import { formatCents, formatPercent, periodLabel } from '../services/format';
import { BillingTabs } from '../components/billing/BillingTabs';

export const STATEMENT_STATUS: Record<string, { label: string; cls: string }> = {
  preview: { label: 'Vorschau', cls: 'bg-slate-100 text-slate-600' },
  draft: { label: 'Entwurf', cls: 'bg-blue-100 text-blue-800' },
  finalized: { label: 'Finalisiert', cls: 'bg-green-100 text-green-800' },
  exported: { label: 'Exportiert', cls: 'bg-slate-200 text-slate-700' },
};

export function BillingStatementsPage() {
  const [params, setParams] = useSearchParams();
  const period = params.get('period') ?? '';
  const status = params.get('status') ?? '';
  const [rows, setRows] = useState<StatementSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    listStatements({ period: period || undefined, status: status || undefined })
      .then(setRows).catch((e) => setError(extractApiError(e))).finally(() => setLoading(false));
  }, [period, status]);

  const periods = Array.from(new Set(rows.map((r) => r.periodKey))).sort().reverse();
  const totals = rows.reduce((a, r) => ({ net: a.net + r.netCents, cost: a.cost + r.costCents }), { net: 0, cost: 0 });

  return (
    <div>
      <div className="mb-2">
        <h2 className="text-xl font-semibold">Abrechnungen</h2>
        <p className="text-sm text-slate-500">Je Kunde und Periode eine Abrechnung mit Positionen je Tenant. Entwürfe werden bei jedem Abrechnungslauf neu berechnet, finalisierte bleiben unverändert.</p>
      </div>
      <BillingTabs />
      <div className="flex flex-wrap gap-2 mb-3">
        <input value={period} onChange={(e) => setParams((p) => { e.target.value ? p.set('period', e.target.value) : p.delete('period'); return p; })} placeholder="Periode YYYY-MM" className="px-2 py-1 border rounded font-mono w-40" list="periods" />
        <datalist id="periods">{periods.map((p) => <option key={p} value={p} />)}</datalist>
        <select value={status} onChange={(e) => setParams((p) => { e.target.value ? p.set('status', e.target.value) : p.delete('status'); return p; })} className="px-2 py-1 border rounded">
          <option value="">alle Status</option>
          {['draft', 'finalized', 'exported'].map((s) => <option key={s} value={s}>{STATEMENT_STATUS[s].label}</option>)}
        </select>
      </div>
      {error && <div className="bg-red-50 border border-red-200 text-red-800 rounded p-3 mb-4 text-sm">{error}</div>}
      {loading ? <p className="text-slate-500">Lade…</p> : (
        <table className="w-full bg-white shadow rounded text-sm">
          <thead className="bg-slate-100 text-left">
            <tr><th className="p-3">Periode</th><th className="p-3">Kunde</th><th className="p-3">Status</th><th className="p-3 text-right">Tenants</th><th className="p-3 text-right">Netto</th><th className="p-3 text-right">Brutto</th><th className="p-3 text-right">Kosten</th><th className="p-3 text-right">Marge</th><th className="p-3">Rechnungs-Nr.</th></tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t hover:bg-slate-50">
                <td className="p-3">{periodLabel(r.periodKey)}</td>
                <td className="p-3"><Link to={`/billing/statements/${r.id}`} className="text-primary-600 hover:underline font-medium">{r.customer.companyName}</Link></td>
                <td className="p-3"><span className={`badge ${STATEMENT_STATUS[r.status]?.cls ?? ''}`}>{STATEMENT_STATUS[r.status]?.label ?? r.status}</span></td>
                <td className="p-3 text-right">{r.tenantCount}</td>
                <td className="p-3 text-right font-mono">{formatCents(r.netCents)}</td>
                <td className="p-3 text-right font-mono">{formatCents(r.grossCents)}{r.reverseCharge && <span className="text-xs text-slate-400 ml-1">RC</span>}</td>
                <td className="p-3 text-right font-mono text-slate-500">{formatCents(r.costCents)}</td>
                <td className={`p-3 text-right font-mono ${r.marginCents < 0 ? 'text-red-700' : ''}`}>{formatPercent(r.netCents > 0 ? (r.marginCents / r.netCents) * 100 : null)}</td>
                <td className="p-3 font-mono text-xs">{r.externalRef ?? '—'}</td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={9} className="p-6 text-center text-slate-500">Keine Abrechnungen. Starte in der <Link to="/billing" className="underline">Übersicht</Link> einen Abrechnungslauf.</td></tr>}
          </tbody>
          {rows.length > 0 && (
            <tfoot><tr className="border-t bg-slate-50 font-semibold"><td className="p-3" colSpan={4}>Summe ({rows.length})</td><td className="p-3 text-right font-mono">{formatCents(totals.net)}</td><td /><td className="p-3 text-right font-mono">{formatCents(totals.cost)}</td><td className="p-3 text-right font-mono">{formatPercent(totals.net > 0 ? ((totals.net - totals.cost) / totals.net) * 100 : null)}</td><td /></tr></tfoot>
          )}
        </table>
      )}
    </div>
  );
}
